/**
 * MeshInstructor — R&D component for Path C (Speech-to-Mesh) on the
 * `feat/mesh-sync` workstream. NOT wired into production yet.
 *
 * Pipeline overview
 *   1. Load `/brand/instructor-portrait.jpg` into a hidden <img>.
 *   2. Run MediaPipe Tasks Vision FaceLandmarker ONCE on load (image
 *      mode, GPU delegate when available) → 478 landmarks.
 *   3. Cache the 40 lip landmarks (FaceLandmarker exposes
 *      FACE_LANDMARKS_LIPS connector — indices 0, 11-17, 37, 39, 40,
 *      61, 78-88, 95, 178, 191, 267-272, 291, 308-318, 324, 402, 415).
 *   4. On each `useSpeechNarration` boundary event, derive a viseme
 *      target (AA/EE/OO/MM/FF) and animate target offsets for each
 *      lip vertex (jaw drops, corners pull wide, lips round, etc.).
 *   5. WebGL warp: render the portrait to a <canvas>, sample it as a
 *      texture, and use a fragment shader that warps a Delaunay-
 *      triangulated mesh of the lip landmarks toward their target
 *      offsets. 60fps target, fully on-device.
 *
 * Implementation status (Iter 51)
 *   ✅ MediaPipe runtime installed (@mediapipe/tasks-vision)
 *   ✅ FaceLandmarker init + landmarks extraction
 *   ✅ Debug overlay: shows lip-landmark dots over the portrait
 *   🚧 Viseme→landmark-offset mapping (TODO)
 *   🚧 WebGL warp shader + Delaunay triangulation (TODO)
 *
 * Privacy (DPDPA 2026)
 *   The MediaPipe wasm + model files are served from the public
 *   Mediapipe CDN. The portrait image is local to the app. Landmarks
 *   are computed entirely on-device — no network calls happen during
 *   inference. No portion of the photo or landmarks ever leaves the
 *   browser.
 *
 * Reachable at /lab/mesh-instructor (sandbox only).
 */
import { useEffect, useRef, useState } from "react";

const PORTRAIT_URL = "/brand/instructor-portrait.jpg?v=6";
// MediaPipe FaceLandmarker model + runtime — public CDN.
const FACE_LANDMARKER_TASK =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";
const VISION_WASM_BASE = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

// 40 lip landmark indices (per MediaPipe Face Mesh canonical model).
// Outer + inner lip ring used as the warp control vertices.
const LIP_LANDMARKS = [
  // Outer lip
  61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267,
  0, 37, 39, 40, 185, 61,
  // Inner lip
  78, 95, 88, 178, 87, 14, 317, 402, 318, 324, 308, 415, 310, 311, 312,
  13, 82, 81, 80, 191, 78,
];

export default function MeshInstructor() {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [status, setStatus] = useState("idle");
  const [lipPoints, setLipPoints] = useState([]); // [{ x, y }] normalised 0-1
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setStatus("loading-runtime");
        // Lazy import keeps the wasm bundle out of the main app chunk.
        const { FilesetResolver, FaceLandmarker } = await import("@mediapipe/tasks-vision");
        if (cancelled) return;

        const fileset = await FilesetResolver.forVisionTasks(VISION_WASM_BASE);
        if (cancelled) return;
        const landmarker = await FaceLandmarker.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: FACE_LANDMARKER_TASK, delegate: "GPU" },
          runningMode: "IMAGE",
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        });
        if (cancelled) return;

        setStatus("loading-image");
        // Wait for the portrait to be fully decoded.
        const img = imgRef.current;
        if (!img) return;
        if (!img.complete || !img.naturalWidth) {
          await new Promise((res, rej) => {
            img.onload = res;
            img.onerror = rej;
          });
        }
        if (cancelled) return;

        setStatus("detecting");
        const result = landmarker.detect(img);
        if (cancelled) return;

        if (!result.faceLandmarks?.length) {
          setError("No face detected in portrait");
          setStatus("failed");
          return;
        }
        const landmarks = result.faceLandmarks[0];
        const lips = LIP_LANDMARKS.map((idx) => ({
          x: landmarks[idx]?.x ?? 0,
          y: landmarks[idx]?.y ?? 0,
        }));
        setLipPoints(lips);
        setStatus("ready");

        // Draw debug overlay.
        const cv = canvasRef.current;
        if (cv) {
          cv.width = img.naturalWidth;
          cv.height = img.naturalHeight;
          const ctx = cv.getContext("2d");
          ctx.drawImage(img, 0, 0);
          ctx.fillStyle = "rgba(0,255,157,0.85)";
          ctx.strokeStyle = "rgba(0,0,0,0.6)";
          ctx.lineWidth = 1;
          for (const p of lips) {
            const px = p.x * cv.width;
            const py = p.y * cv.height;
            ctx.beginPath();
            ctx.arc(px, py, 3.5, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[mesh-instructor] init failed:", e);
        setError(String(e?.message || e));
        setStatus("failed");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white p-8 flex flex-col items-center gap-6">
      <h1 className="font-display text-2xl">Mesh-Sync R&amp;D — Path C sandbox</h1>
      <p className="text-sm text-white/55 max-w-xl text-center leading-relaxed">
        MediaPipe FaceLandmarker runs on-device → extracts 40 lip vertices on the realistic
        portrait. Status:{" "}
        <code className="font-mono-oak text-[#00FF9D]" data-testid="mesh-status">{status}</code>.
        {error && (
          <span className="block mt-2 text-rose-300" data-testid="mesh-error">
            {error}
          </span>
        )}
      </p>

      {/* Hidden <img> — used as the FaceLandmarker input. Decoded
          eagerly so the detection step doesn't await a network round-
          trip after model init. */}
      <img
        ref={imgRef}
        src={PORTRAIT_URL}
        alt=""
        crossOrigin="anonymous"
        style={{ display: "none" }}
        decoding="async"
      />

      <canvas
        ref={canvasRef}
        className="rounded-2xl border border-white/10 max-w-[480px] max-h-[600px] w-full h-auto"
        data-testid="mesh-canvas"
      />

      <div className="text-[11px] text-white/45 max-w-xl text-center leading-relaxed">
        <strong>What's working:</strong> on-device FaceLandmarker init, lip vertex extraction,
        debug overlay (green dots over her lip ring). All processing in-browser, zero network
        traffic during inference (DPDPA 2026 compliant).
        <br />
        <strong>What's next:</strong> Viseme → vertex offset map, Delaunay triangulation, WebGL
        fragment-shader image warp at 60fps. Estimated 3–5 days focused dev. Lip vertex
        count:{" "}
        <code className="font-mono-oak">{lipPoints.length}</code>.
      </div>
    </div>
  );
}
